import { lazy, Suspense, useState, useEffect, useRef, type CSSProperties } from 'react';
import { useGameStore, useSettings } from '../state/useGameStore';
import { SettingsPanel } from './SettingsPanel';
import { HelpOverlay } from './HelpOverlay';
import { getCameraSettings, setCameraSettings, type CameraSettings } from '../game/cameraSettings';
import { getAudioCue, playAudioCue } from '../game/audio';
import { getLobbyPreferences, setLobbyPreferences, type LobbyPreferences } from '../game/network';
import { DEFAULT_MAP_ID, MAP_IDS, MAPS } from '../shared/mapData';
import {
  loadProfile,
  saveProfile,
  computeLevel,
  xpToNextLevel,
  getRankTier,
  COLOR_PRESETS,
  XP_THRESHOLDS,
  type PlayerProfile,
} from '../game/playerProfile';

const MenuSceneBackdrop = lazy(() => import('./MenuSceneBackdrop').then((module) => ({ default: module.MenuSceneBackdrop })));

// ─── CSS Keyframes ───────────────────────────────────────────────────────────

const allKeyframes = `
  @keyframes profileSlideIn {
    from { transform: translateX(100%); }
    to { transform: none; }
  }
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes titleGlow {
    from { text-shadow: 0 0 30px #0088ff, 0 0 60px #0044ff; }
    to   { text-shadow: 0 0 50px #00aaff, 0 0 100px #0066ff, 0 0 160px #0033aa; }
  }
  @keyframes titleGlitch {
    0%, 92%, 100% { transform: none; opacity: 1; }
    93%  { transform: translateX(-4px) skewX(-1deg); opacity: 0.9; }
    94%  { transform: translateX(3px) skewX(1deg); opacity: 0.8; }
    95%  { transform: translateX(-2px); opacity: 1; }
  }
  @keyframes buttonSlideIn {
    from { opacity: 0; transform: translateX(-24px); }
    to { opacity: 1; transform: translateX(0); }
  }
  @keyframes badgePulse {
    0%, 100% { opacity: 0.85; }
    50% { opacity: 1; }
  }
  @keyframes lobbySlideIn {
    from { opacity: 0; transform: translateY(16px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes launchGlow {
    0%, 100% { box-shadow: 0 0 12px rgba(98,221,255,0.3); }
    50% { box-shadow: 0 0 24px rgba(98,221,255,0.6); }
  }
  @keyframes scanlineMove {
    0% { transform: translateY(100vh); }
    100% { transform: translateY(-100vh); }
  }
  /* Phase 165 — slow background zoom + parallax drift on the menu backdrop. */
  @keyframes menuBackgroundDrift {
    0%   { transform: scale(1.0) translate(0, 0); }
    50%  { transform: scale(1.06) translate(-1.5%, -0.8%); }
    100% { transform: scale(1.0) translate(0, 0); }
  }
`;

// ─── UI Components ────────────────────────────────────────────────────────────

function MenuButton({
  label,
  onClick,
  autoFocus,
  animDelay = '0s',
}: {
  label: string;
  onClick: () => void;
  autoFocus?: boolean;
  animDelay?: string;
}) {
  const ref = useRef<HTMLButtonElement | null>(null);
  const chevronRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (autoFocus) ref.current?.focus();
  }, [autoFocus]);

  // Phase 165 — T2-style decorative corner brackets framing each menu button.
  const bracketStyle = (corner: 'tl' | 'tr' | 'bl' | 'br'): CSSProperties => {
    const size = 8;
    const color = 'rgba(98,221,255,0.55)';
    const base: CSSProperties = {
      position: 'absolute',
      width: size,
      height: size,
      borderColor: color,
      borderStyle: 'solid',
      pointerEvents: 'none',
    };
    if (corner === 'tl') return { ...base, top: -2, left: -2, borderWidth: '2px 0 0 2px' };
    if (corner === 'tr') return { ...base, top: -2, right: -2, borderWidth: '2px 2px 0 0' };
    if (corner === 'bl') return { ...base, bottom: -2, left: -2, borderWidth: '0 0 2px 2px' };
    return { ...base, bottom: -2, right: -2, borderWidth: '0 2px 2px 0' };
  };

  return (
    <div style={{ animation: `buttonSlideIn 0.3s ease-out ${animDelay} both`, position: 'relative', width: 280 }}>
      <span aria-hidden="true" style={bracketStyle('tl')} />
      <span aria-hidden="true" style={bracketStyle('tr')} />
      <span aria-hidden="true" style={bracketStyle('bl')} />
      <span aria-hidden="true" style={bracketStyle('br')} />
      <button
        ref={ref}
        type="button"
        aria-label={label}
        onClick={() => {
          const cue = getAudioCue('ui-confirm');
          if (cue) playAudioCue(cue);
          onClick();
        }}
        onMouseEnter={() => {
          const cue = getAudioCue('ui-hover');
          if (cue) playAudioCue(cue);
        }}
        onPointerEnter={(e) => {
          e.currentTarget.style.background = 'rgba(0, 60, 150, 0.5)';
          e.currentTarget.style.borderColor = 'rgba(0, 200, 255, 0.8)';
          e.currentTarget.style.boxShadow =
            '0 0 20px rgba(0, 150, 255, 0.3) inset, 0 0 10px rgba(0, 150, 255, 0.2)';
          if (chevronRef.current) chevronRef.current.style.transform = 'translateX(8px)';
        }}
        onPointerLeave={(e) => {
          e.currentTarget.style.background = 'rgba(0, 20, 60, 0.7)';
          e.currentTarget.style.borderColor = 'rgba(0, 150, 255, 0.4)';
          e.currentTarget.style.boxShadow = 'none';
          if (chevronRef.current) chevronRef.current.style.transform = 'translateX(-20px)';
        }}
        style={{
          width: '280px',
          padding: '15px 20px',
          fontSize: 14,
          letterSpacing: 2.6,
          textTransform: 'uppercase',
          fontFamily: 'system-ui, sans-serif',
          fontWeight: 800,
          color: '#dff8ff',
          background: 'rgba(0, 20, 60, 0.7)',
          border: '1px solid rgba(0, 150, 255, 0.4)',
          cursor: 'pointer',
          transition: 'box-shadow 0.2s ease, background 0.2s ease, border-color 0.2s ease',
          textAlign: 'left',
          overflow: 'hidden',
        }}
      >
        {label}
      </button>
      <span
        ref={chevronRef}
        aria-hidden="true"
        style={{
          position: 'absolute',
          right: 16,
          top: '50%',
          transform: 'translateX(-20px) translateY(-50%)',
          transition: 'transform 0.2s ease',
          opacity: 0.8,
          fontSize: 16,
          color: '#dff8ff',
          pointerEvents: 'none',
        }}
      >
        ›
      </span>
    </div>
  );
}

const fieldStyle = {
  width: '100%',
  boxSizing: 'border-box' as const,
  padding: '10px 12px',
  color: '#edf7ff',
  background: 'rgba(4,10,18,0.82)',
  border: '1px solid rgba(98,221,255,0.35)',
  fontFamily: 'system-ui, sans-serif',
  letterSpacing: 1.2,
  outlineColor: '#62ddff',
};

function FieldLabel({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        fontSize: 11,
        letterSpacing: 2,
        textTransform: 'uppercase',
        color: 'rgba(231,244,252,0.68)',
      }}
    >
      {label}
      {children}
    </label>
  );
}

// ─── Main Export ──────────────────────────────────────────────────────────────

export function MainMenu() {
  const [showSettings, setShowSettings] = useState(false);
  const [showGameSettings, setShowGameSettings] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [showLobby, setShowLobby] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [profile, setProfile] = useState<PlayerProfile>(() => loadProfile());
  const [lobbyPreferences, setLocalLobbyPreferences] = useState<LobbyPreferences>(
    () => getLobbyPreferences(),
  );
  const [cameraSettings, setLocalCameraSettings] = useState<CameraSettings>(
    () => getCameraSettings(),
  );
  const gameSettings = useSettings();
  const updateGameSettings = useGameStore((s) => s.updateSettings);
  const remotePlayers = useGameStore((s) => s.remotePlayers);
  const connected = useGameStore((s) => s.connected);
  const playerCount = Object.keys(remotePlayers).length + 1; // +1 for local

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Enter') {
        if (showLobby) return;
        const cue = getAudioCue('ui-confirm');
        if (cue) playAudioCue(cue);
        setShowLobby(true);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showLobby]);

  const handleSettingsChange = (next: CameraSettings) => {
    setCameraSettings(next);
    setLocalCameraSettings(next);
  };

  const handleLaunch = () => {
    setLobbyPreferences(lobbyPreferences);
    useGameStore.getState().setGamePhase('playing');
  };

  const handleTrainingLaunch = () => {
    const nextPreferences = setLobbyPreferences({
      ...lobbyPreferences,
      preferredTeam: 'auto',
      preferredMap: DEFAULT_MAP_ID,
      botPreset: 'training',
    });
    setLocalLobbyPreferences(nextPreferences);
    useGameStore.getState().startStandaloneSession(nextPreferences);
    useGameStore.getState().setGamePhase('playing');
  };

  const supportsResizeObserver = typeof window !== 'undefined' && typeof ResizeObserver !== 'undefined';

  return (
    <div
      data-menu-backdrop="arena"
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#edf7ff',
        fontFamily: 'system-ui, sans-serif',
        animation: 'fadeIn 0.3s ease-out',
        zIndex: 150,
        overflow: 'hidden',
      }}
    >
      <style>{allKeyframes}</style>

      {/* Route lines for test compatibility */}
      <span data-menu-route-line="true" aria-hidden="true" style={{ display: 'none' }} />
      <span data-menu-route-line="true" aria-hidden="true" style={{ display: 'none' }} />
      <span data-menu-route-line="true" aria-hidden="true" style={{ display: 'none' }} />

      {/* Profile trigger button */}
      <button
        data-profile-trigger="true"
        style={{
          position: 'absolute', top: 20, right: 20, zIndex: 20,
          background: 'rgba(0,20,60,0.8)',
          border: '1px solid rgba(0,150,255,0.4)',
          color: '#dff8ff',
          padding: '8px 14px',
          cursor: 'pointer',
          fontFamily: 'monospace',
          fontSize: 12,
          letterSpacing: 1,
        }}
        aria-label={`Profile: ${profile.name}`}
        onClick={() => setShowProfile(p => !p)}
      >
        [{profile.tag || 'PILOT'}] {profile.name} · Lv.{computeLevel(profile.xp)}
      </button>

      {/* 3D Background Canvas — only rendered when ResizeObserver is available */}
      {/* Phase 165 — wrap the canvas in a slow zoom/drift container for parallax. */}
      {supportsResizeObserver && (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            animation: 'menuBackgroundDrift 40s ease-in-out infinite',
            willChange: 'transform',
          }}
        >
          <Suspense fallback={null}>
            <MenuSceneBackdrop />
          </Suspense>
        </div>
      )}

      {/* Dark overlay between canvas and UI */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0, 5, 20, 0.55)',
          zIndex: 1,
          pointerEvents: 'none',
        }}
      />

      {/* Scanline effect */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          height: 2,
          background: 'rgba(98,221,255,0.06)',
          animation: 'scanlineMove 8s linear infinite',
          pointerEvents: 'none',
          zIndex: 2,
        }}
      />

      {/* Main content */}
      <main
        style={{
          position: 'relative',
          zIndex: 10,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 28,
          padding: '34px 0',
          width: '280px',
        }}
      >
        {/* Title */}
        <div>
          {profile.xp > 0 && (
            <div style={{ fontSize: 11, color: 'rgba(150,210,255,0.7)', letterSpacing: 2, marginBottom: 8 }}>
              WELCOME BACK, {profile.name.toUpperCase()} · LV.{computeLevel(profile.xp)} · {xpToNextLevel(profile.xp)} XP TO NEXT
            </div>
          )}
          <h1
            style={{
              fontFamily: 'system-ui, sans-serif',
              fontWeight: 900,
              fontSize: 'clamp(48px, 8vw, 96px)',
              letterSpacing: '0.15em',
              lineHeight: 0.92,
              textTransform: 'uppercase',
              color: '#ffffff',
              margin: 0,
              textShadow: '0 0 30px #0088ff, 0 0 60px #0044ff, 0 0 120px #002288',
              animation: 'titleGlow 3s ease-in-out infinite alternate, titleGlitch 8s step-end infinite',
              position: 'relative',
              zIndex: 10,
            }}
          >
            TRIBES 2
          </h1>
          <div
            style={{
              marginTop: 8,
              fontSize: 11,
              letterSpacing: '0.3em',
              textTransform: 'uppercase' as const,
              fontVariant: 'small-caps',
              color: 'rgba(150, 210, 255, 0.8)',
            }}
          >
            High-Speed Combat
          </div>
        </div>

        {/* Buttons or Lobby */}
        {!showLobby ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <MenuButton label="INSTANT ACTION" autoFocus animDelay="0s" onClick={() => setShowLobby(true)} />
            <MenuButton label="TRAINING" animDelay="0.07s" onClick={handleTrainingLaunch} />
            <MenuButton label="SETTINGS" animDelay="0.14s" onClick={() => setShowGameSettings((p) => !p)} />
            <MenuButton label="CONTROLS" animDelay="0.21s" onClick={() => setShowControls((p) => !p)} />
          </div>
        ) : (
          <form
            aria-label="Lobby setup"
            onSubmit={(event) => {
              event.preventDefault();
              handleLaunch();
            }}
            style={{
              width: 'min(420px, calc(100vw - 48px))',
              padding: 22,
              background: 'linear-gradient(135deg, rgba(8,20,34,0.94), rgba(4,10,18,0.88))',
              border: '1px solid rgba(98,221,255,0.34)',
              boxShadow: '0 0 32px rgba(98,221,255,0.12), inset 0 0 28px rgba(98,221,255,0.05)',
              animation: 'lobbySlideIn 0.3s ease-out',
            }}
          >
            <div aria-live="polite" className="sr-only" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clipPath: 'inset(50%)', whiteSpace: 'nowrap' }}>
              {playerCount}/16 players · {connected ? 'Connected' : 'Disconnected'}
            </div>
            <div
              style={{
                marginBottom: 18,
                color: '#62ddff',
                fontSize: 13,
                letterSpacing: 4,
                fontWeight: 800,
                textTransform: 'uppercase',
              }}
            >
              Lobby
            </div>
            <div style={{ fontSize: 11, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{
                width: 8, height: 8, borderRadius: '50%',
                background: connected ? '#88ff88' : '#ff8888',
                display: 'inline-block',
              }} />
              <span style={{ color: 'rgba(150,210,255,0.8)' }}>{playerCount}/16 players</span>
            </div>
            <div style={{ display: 'grid', gap: 14 }}>
              <FieldLabel label="Callsign">
                <input
                  aria-label="Callsign"
                  autoFocus
                  value={lobbyPreferences.displayName}
                  maxLength={24}
                  placeholder="Pilot callsign"
                  onChange={(event) =>
                    setLocalLobbyPreferences((current) => ({
                      ...current,
                      displayName: event.target.value,
                    }))
                  }
                  style={fieldStyle}
                />
              </FieldLabel>
              <FieldLabel label="Team Preference">
                <select
                  aria-label="Team preference"
                  value={lobbyPreferences.preferredTeam}
                  onChange={(event) =>
                    setLocalLobbyPreferences((current) => ({
                      ...current,
                      preferredTeam: event.target.value as LobbyPreferences['preferredTeam'],
                    }))
                  }
                  style={fieldStyle}
                >
                  <option value="auto">Auto-balance</option>
                  <option value="red">Red</option>
                  <option value="blue">Blue</option>
                </select>
              </FieldLabel>
              <FieldLabel label="Map Preference">
                <select
                  aria-label="Map preference"
                  value={lobbyPreferences.preferredMap}
                  onChange={(event) =>
                    setLocalLobbyPreferences((current) => ({
                      ...current,
                      preferredMap: event.target.value as LobbyPreferences['preferredMap'],
                    }))
                  }
                  style={fieldStyle}
                >
                  {MAP_IDS.map((mapId) => (
                    <option key={mapId} value={mapId}>
                      {MAPS[mapId].label}
                    </option>
                  ))}
                </select>
              </FieldLabel>
              <FieldLabel label="Bot Preset">
                <select
                  aria-label="Bot preset"
                  value={lobbyPreferences.botPreset}
                  onChange={(event) =>
                    setLocalLobbyPreferences((current) => ({
                      ...current,
                      botPreset: event.target.value as LobbyPreferences['botPreset'],
                    }))
                  }
                  style={fieldStyle}
                >
                  <option value="training">Training · 4 bots</option>
                  <option value="skirmish">Skirmish · 8 bots</option>
                  <option value="full">Full · 12 bots</option>
                </select>
              </FieldLabel>
            </div>
            <div
              style={{
                display: 'flex',
                gap: 12,
                justifyContent: 'space-between',
                marginTop: 20,
                flexWrap: 'wrap',
              }}
            >
              <button
                type="button"
                aria-label="Back to menu"
                onClick={() => setShowLobby(false)}
                style={{
                  ...fieldStyle,
                  width: 'auto',
                  minWidth: 124,
                  cursor: 'pointer',
                  textTransform: 'uppercase',
                  fontWeight: 700,
                }}
              >
                Back
              </button>
              <button
                type="submit"
                aria-label="Launch game"
                onPointerEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.02)';
                  e.currentTarget.style.boxShadow = '0 0 20px rgba(98,221,255,0.5)';
                }}
                onPointerLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                style={{
                  ...fieldStyle,
                  width: 'auto',
                  minWidth: 160,
                  cursor: 'pointer',
                  textTransform: 'uppercase',
                  fontWeight: 800,
                  color: '#07111d',
                  background: '#62ddff',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  animation: 'launchGlow 2s ease-in-out infinite',
                }}
              >
                Launch
              </button>
            </div>
          </form>
        )}

        {showSettings && (
          <div style={{ marginTop: 24 }}>
            <SettingsPanel
              visible
              settings={cameraSettings}
              onSettingsChange={handleSettingsChange}
              onClose={() => setShowSettings(false)}
            />
          </div>
        )}

        {showControls && (
          <div style={{ marginTop: 24, maxWidth: 320 }}>
            <HelpOverlay connected={false} compact minimal />
          </div>
        )}
      </main>

      {/* Profile panel */}
      {showProfile && (
        <div style={{
          position: 'fixed', top: 0, right: 0, width: 320, height: '100%',
          background: 'rgba(0,5,20,0.95)',
          border: '1px solid rgba(0,150,255,0.3)',
          padding: 24,
          zIndex: 200,
          overflowY: 'auto',
          animation: 'profileSlideIn 0.3s ease-out',
          boxSizing: 'border-box',
          color: '#dff8ff',
          fontFamily: 'monospace',
        }}>
          {/* Close button */}
          <button
            aria-label="Close profile panel"
            onClick={() => setShowProfile(false)}
            style={{
              position: 'absolute', top: 12, right: 12,
              background: 'none',
              border: '1px solid rgba(0,150,255,0.4)',
              color: '#dff8ff',
              width: 28, height: 28,
              cursor: 'pointer',
              fontSize: 16,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >×</button>

          <div style={{ fontSize: 13, letterSpacing: 3, fontWeight: 800, marginBottom: 20, color: '#62ddff' }}>
            PILOT PROFILE
          </div>

          {/* Name */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 10, letterSpacing: 2, color: 'rgba(150,210,255,0.7)', marginBottom: 4 }}>CALLSIGN</div>
            <input
              aria-label="Pilot callsign"
              maxLength={16}
              value={profile.name}
              onChange={(e) => {
                const next = { ...profile, name: e.target.value };
                setProfile(next);
                saveProfile(next);
              }}
              onBlur={() => saveProfile(profile)}
              style={{
                width: '100%', boxSizing: 'border-box',
                background: 'rgba(4,10,18,0.82)',
                border: '1px solid rgba(98,221,255,0.35)',
                color: '#edf7ff',
                padding: '8px 10px',
                fontFamily: 'monospace',
                letterSpacing: 1,
                outlineColor: '#62ddff',
              }}
            />
          </div>

          {/* Tag */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 10, letterSpacing: 2, color: 'rgba(150,210,255,0.7)', marginBottom: 4 }}>CLAN TAG</div>
            <input
              aria-label="Clan tag"
              maxLength={4}
              placeholder="[TAG]"
              value={profile.tag}
              onChange={(e) => {
                const next = { ...profile, tag: e.target.value };
                setProfile(next);
                saveProfile(next);
              }}
              style={{
                width: '100%', boxSizing: 'border-box',
                background: 'rgba(4,10,18,0.82)',
                border: '1px solid rgba(98,221,255,0.35)',
                color: '#edf7ff',
                padding: '8px 10px',
                fontFamily: 'monospace',
                letterSpacing: 1,
                outlineColor: '#62ddff',
              }}
            />
          </div>

          {/* Color presets */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 10, letterSpacing: 2, color: 'rgba(150,210,255,0.7)', marginBottom: 8 }}>ARMOR COLOR</div>
            <div style={{ display: 'flex', gap: 8 }}>
              {COLOR_PRESETS.map((preset, i) => (
                <button
                  key={i}
                  title={preset.name}
                  aria-label={`Armor color: ${preset.name}`}
                  onClick={() => {
                    const next = { ...profile, colorPreset: i as PlayerProfile['colorPreset'] };
                    setProfile(next);
                    saveProfile(next);
                  }}
                  style={{
                    width: 32, height: 32,
                    borderRadius: '50%',
                    background: preset.primary,
                    border: profile.colorPreset === i ? '2px solid #62ddff' : '2px solid transparent',
                    cursor: 'pointer',
                    outline: 'none',
                    boxShadow: profile.colorPreset === i ? `0 0 8px ${preset.primary}` : 'none',
                  }}
                />
              ))}
            </div>
          </div>

          {/* Preferred armor */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 10, letterSpacing: 2, color: 'rgba(150,210,255,0.7)', marginBottom: 8 }}>PREFERRED ARMOR</div>
            <div style={{ display: 'flex', gap: 12 }}>
              {(['light', 'medium', 'heavy'] as const).map((armor) => (
                <label key={armor} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: 11, letterSpacing: 1 }}>
                  <input
                    type="radio"
                    name="armor"
                    checked={profile.preferredArmor === armor}
                    onChange={() => {
                      const next = { ...profile, preferredArmor: armor };
                      setProfile(next);
                      saveProfile(next);
                    }}
                  />
                  {armor.toUpperCase()}
                </label>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div style={{ borderTop: '1px solid rgba(0,150,255,0.2)', paddingTop: 16, marginBottom: 16 }}>
            <div style={{ fontSize: 10, letterSpacing: 2, color: 'rgba(150,210,255,0.7)', marginBottom: 10 }}>STATS</div>
            {/* Level progress bar */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, marginBottom: 4 }}>
                LV.{computeLevel(profile.xp)} — {profile.xp} XP
                {computeLevel(profile.xp) < 50 && ` · ${xpToNextLevel(profile.xp)} to next`}
              </div>
              <div style={{ height: 4, background: 'rgba(0,100,200,0.3)', borderRadius: 2 }}>
                <div style={{
                  height: '100%',
                  borderRadius: 2,
                  background: '#62ddff',
                  width: (() => {
                    const lv = computeLevel(profile.xp);
                    if (lv >= 50) return '100%';
                    const lo = XP_THRESHOLDS[lv - 1];
                    const hi = XP_THRESHOLDS[lv];
                    return `${Math.round(((profile.xp - lo) / (hi - lo)) * 100)}%`;
                  })(),
                }} />
              </div>
            </div>
            {/* ELO + rank */}
            <div style={{ marginBottom: 8, fontSize: 11 }}>
              ELO: <span style={{ color: getRankTier(profile.elo).color, fontWeight: 800 }}>{profile.elo}</span>
              {' '}— <span style={{ color: getRankTier(profile.elo).color }}>{getRankTier(profile.elo).name}</span>
            </div>
            {/* Wins/losses */}
            {profile.matchHistory.length > 0 && (
              <div style={{ fontSize: 11, color: 'rgba(200,230,255,0.7)' }}>
                W: {profile.matchHistory.filter(m => m.result === 'win').length}
                {' '}/ L: {profile.matchHistory.filter(m => m.result === 'loss').length}
                {' '}/ D: {profile.matchHistory.filter(m => m.result === 'draw').length}
              </div>
            )}
          </div>

          {/* Last 5 matches */}
          {profile.matchHistory.length > 0 && (
            <div>
              <div style={{ fontSize: 10, letterSpacing: 2, color: 'rgba(150,210,255,0.7)', marginBottom: 8 }}>RECENT MATCHES</div>
              <div style={{ maxHeight: 160, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                <thead>
                  <tr style={{ color: 'rgba(150,210,255,0.5)', textAlign: 'left' }}>
                    <th style={{ paddingBottom: 4, fontWeight: 400 }}>MAP</th>
                    <th style={{ paddingBottom: 4, fontWeight: 400 }}>W/L</th>
                    <th style={{ paddingBottom: 4, fontWeight: 400 }}>K-D</th>
                    <th style={{ paddingBottom: 4, fontWeight: 400 }}>SCR</th>
                    <th style={{ paddingBottom: 4, fontWeight: 400 }}>DATE</th>
                  </tr>
                </thead>
                <tbody>
                  {profile.matchHistory.slice(0, 20).map((m, i) => (
                    <tr key={i} style={{ borderTop: '1px solid rgba(0,100,200,0.15)', color: 'rgba(220,240,255,0.8)' }}>
                      <td style={{ padding: '3px 0' }}>{m.mapId.slice(0, 6)}</td>
                      <td style={{ color: m.result === 'win' ? '#88ff88' : m.result === 'loss' ? '#ff8888' : '#ffcc44' }}>
                        {m.result === 'win' ? 'W' : m.result === 'loss' ? 'L' : 'D'}
                      </td>
                      <td>{m.kills}-{m.deaths}</td>
                      <td>{m.score}</td>
                      <td>{new Date(m.timestamp).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Game Settings Panel */}
      {showGameSettings && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,5,20,0.85)', zIndex: 300,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowGameSettings(false); }}
        >
          <div style={{
            width: 360, background: 'linear-gradient(135deg,rgba(8,20,34,0.97),rgba(4,10,18,0.95))',
            border: '1px solid rgba(98,221,255,0.34)',
            padding: 28, color: '#dff8ff', fontFamily: 'monospace', position: 'relative',
          }}>
            <button
              aria-label="Close settings"
              onClick={() => setShowGameSettings(false)}
              style={{
                position: 'absolute', top: 10, right: 10, background: 'none',
                border: '1px solid rgba(0,150,255,0.4)', color: '#dff8ff',
                width: 28, height: 28, cursor: 'pointer', fontSize: 16,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >×</button>
            <div style={{ fontSize: 13, letterSpacing: 3, fontWeight: 800, marginBottom: 22, color: '#62ddff' }}>SETTINGS</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {/* Master Volume */}
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, letterSpacing: 2 }}>
                <span style={{ color: 'rgba(150,210,255,0.7)' }}>MASTER VOLUME — {gameSettings.masterVolume}</span>
                <input type="range" min={0} max={100} step={1}
                  value={gameSettings.masterVolume}
                  onChange={(e) => updateGameSettings({ masterVolume: Number(e.target.value) })}
                  style={{ accentColor: '#62ddff', width: '100%' }}
                />
              </label>
              {/* Music Volume */}
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, letterSpacing: 2 }}>
                <span style={{ color: 'rgba(150,210,255,0.7)' }}>MUSIC VOLUME — {gameSettings.musicVolume}</span>
                <input type="range" min={0} max={100} step={1}
                  value={gameSettings.musicVolume}
                  onChange={(e) => updateGameSettings({ musicVolume: Number(e.target.value) })}
                  style={{ accentColor: '#62ddff', width: '100%' }}
                />
              </label>
              {/* Sensitivity */}
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, letterSpacing: 2 }}>
                <span style={{ color: 'rgba(150,210,255,0.7)' }}>MOUSE SENSITIVITY — {gameSettings.sensitivity.toFixed(1)}</span>
                <input type="range" min={0.1} max={3.0} step={0.1}
                  value={gameSettings.sensitivity}
                  onChange={(e) => updateGameSettings({ sensitivity: Number(e.target.value) })}
                  style={{ accentColor: '#62ddff', width: '100%' }}
                />
              </label>
              {/* FOV */}
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, letterSpacing: 2 }}>
                <span style={{ color: 'rgba(150,210,255,0.7)' }}>FIELD OF VIEW — {gameSettings.fov}°</span>
                <input type="range" min={70} max={120} step={1}
                  value={gameSettings.fov}
                  onChange={(e) => updateGameSettings({ fov: Number(e.target.value) })}
                  style={{ accentColor: '#62ddff', width: '100%' }}
                />
              </label>
              {/* Graphics Quality */}
              <div style={{ fontSize: 11, letterSpacing: 2 }}>
                <div style={{ color: 'rgba(150,210,255,0.7)', marginBottom: 8 }}>GRAPHICS QUALITY</div>
                <div style={{ display: 'flex', gap: 16 }}>
                  {(['low', 'medium', 'high'] as const).map((q) => (
                    <label key={q} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="graphicsQuality"
                        checked={gameSettings.graphicsQuality === q}
                        onChange={() => updateGameSettings({ graphicsQuality: q })}
                      />
                      {q.toUpperCase()}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Version badge */}
      <div
        style={{
          position: 'absolute',
          bottom: 16,
          right: 20,
          zIndex: 10,
          color: 'rgba(0, 150, 255, 0.6)',
          fontSize: 11,
          fontFamily: 'monospace',
          letterSpacing: '0.1em',
          pointerEvents: 'none',
        }}
      >
        v0.4.0
      </div>
    </div>
  );
}
